import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  Campaign,
  CampaignStatus,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  ListCampaignsParams,
} from '../../../infrastructure/CampaignClient';
import { CampaignClient } from '../../../infrastructure/CampaignClient';
import { getConfig } from '../../../config';

const campaignClient = new CampaignClient(getConfig().apiUrl);

export const useCampaign = () => {
  const queryClient = useQueryClient();

  const campaignsQuery = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => campaignClient.listCampaigns(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const createCampaignMutation = useMutation({
    mutationFn: (request: CreateCampaignRequest) => {
      // Use the selected date/time as UTC directly
      return campaignClient.createCampaign(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const updateCampaignMutation = useMutation({
    mutationFn: ({ id, request }: { id: number; request: UpdateCampaignRequest }) => {
      // Use the selected date/time as UTC directly
      return campaignClient.updateCampaign(id, request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const updateCampaignStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: CampaignStatus }) =>
      campaignClient.updateCampaignStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const deleteCampaignMutation = useMutation({
    mutationFn: (id: number) => campaignClient.deleteCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const getCampaignQuery = (id: number) =>
    useQuery({
      queryKey: ['campaign', id],
      queryFn: () => campaignClient.getCampaign(id),
      enabled: Boolean(id),
    });

  const listCampaignsWithFilter = (params?: ListCampaignsParams) =>
    useQuery({
      queryKey: ['campaigns', params],
      queryFn: () => campaignClient.listCampaigns(params),
      staleTime: 2 * 60 * 1000, // 2 minutes for filtered results
    });

  return {
    // Queries
    campaignsQuery,
    getCampaignQuery,
    listCampaignsWithFilter,

    // Mutations
    createCampaignMutation,
    updateCampaignMutation,
    updateCampaignStatusMutation,
    deleteCampaignMutation,

    // Direct client access for specific needs
    campaignClient,
  };
};

export type { Campaign, CampaignStatus, CreateCampaignRequest, UpdateCampaignRequest };
